import api from './authService'
import React from 'react'
import { crearErrorAplicacionApi } from './apiError'

export interface MetricaReporte {
  id: string
  titulo: string
  nombre?: string
  valor: number
  cambio: number
  tipo: string
  icono: string | React.ReactNode
  color: string
  unidad: string
  descripcion?: string
  valorAnterior: number
  tendencia?: 'subida' | 'bajada' | 'estable'
}

export interface DatosGrafico {
  fecha: string
  valor?: number
  categoria?: string
  tipo?: string
  usuarios: number
  descargas: number
  uploads: number
  ingresos: number
  sesiones: number
}

export interface ReportePersonalizado {
  id: string
  nombre: string
  descripcion?: string
  tipo: 'usuarios' | 'archivos' | 'actividad' | 'sistema' | 'rendimiento' | 'financiero'
  estado: 'activo' | 'pausado' | 'completado' | 'error'
  fechaCreacion: string
  ultimaEjecucion?: string
  proximaEjecucion?: string
  frecuencia?: 'diario' | 'semanal' | 'mensual' | 'manual'
  parametros: Record<string, any>
  resultados?: any
}

export interface FiltrosReporte {
  fechaInicio?: string
  fechaFin?: string
  tipo?: string
  categoria?: string
  usuario?: string
  granularidad?: string
  tipoReporte?: string
}

export interface DashboardData {
  metricas: MetricaReporte[]
  graficos: {
    actividad: DatosGrafico[]
    categorias: DatosGrafico[]
    usuarios: DatosGrafico[]
    roles: DatosGrafico[]
  }
  distribucionUsuarios: {
    porRol: Record<string, number>
    porRolGlobal: Record<string, number>
    porTipoOrganizacion: Record<string, number>
    porRolOrganizacion: {
      institucion: Record<string, number>
      empresa: Record<string, number>
    }
  }
  actividadReciente: any[]
}

export const reportesService = {
  /**
   * Obtener datos del dashboard principal
   */
  async obtenerDashboard(filtros?: FiltrosReporte): Promise<DashboardData> {
    try {
      const params = new URLSearchParams()
      if (filtros?.fechaInicio) params.append('fecha_inicio', filtros.fechaInicio)
      if (filtros?.fechaFin) params.append('fecha_fin', filtros.fechaFin)
      if (filtros?.granularidad) params.append('granularidad', filtros.granularidad)

      const response = await api.get(`/api/reportes/dashboard?${params.toString()}`)
      const data = response.data

      return {
        metricas: data.metricas?.map((metrica: any) => this.normalizarMetrica(metrica)) || [],
        graficos: {
          actividad: data.graficos?.actividad?.map((punto: any) => this.normalizarDatosGrafico(punto)) || [],
          categorias: data.graficos?.categorias?.map((punto: any) => this.normalizarDatosGrafico(punto)) || [],
          usuarios: data.graficos?.usuarios?.map((punto: any) => this.normalizarDatosGrafico(punto)) || [],
          roles: data.graficos?.roles?.map((punto: any) => this.normalizarDatosGrafico(punto)) || [],
        },
        distribucionUsuarios: data.distribucion_usuarios || {
          porRol: {},
          porRolGlobal: {},
          porTipoOrganizacion: {},
          porRolOrganizacion: { institucion: {}, empresa: {} },
        },
        actividadReciente: data.actividad_reciente || data.recent_activity || [],
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener datos del dashboard')
    }
  },

  /**
   * Obtener métricas específicas
   */
  async obtenerMetricas(tipoOrFiltros?: string | FiltrosReporte, filtros?: FiltrosReporte): Promise<MetricaReporte[]> {
    try {
      const params = new URLSearchParams()
      if (typeof tipoOrFiltros === 'string') {
        params.append('tipo', tipoOrFiltros)
      } else if (tipoOrFiltros) {
        const f = tipoOrFiltros
        if (f.fechaInicio) params.append('fecha_inicio', f.fechaInicio)
        if (f.fechaFin) params.append('fecha_fin', f.fechaFin)
        if (f.tipo) params.append('tipo', f.tipo)
      }
      if (filtros?.fechaInicio) params.append('fecha_inicio', filtros.fechaInicio)
      if (filtros?.fechaFin) params.append('fecha_fin', filtros.fechaFin)

      const response = await api.get(`/api/reportes/metricas?${params.toString()}`)
      return response.data.map((metrica: any) => this.normalizarMetrica(metrica))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener métricas')
    }
  },

  /**
   * Obtener datos para gráficos
   */
  async obtenerDatosGrafico(tipo: string, filtros?: FiltrosReporte): Promise<DatosGrafico[]> {
    try {
      const params = new URLSearchParams()
      params.append('tipo', tipo)
      if (filtros?.fechaInicio) params.append('fecha_inicio', filtros.fechaInicio)
      if (filtros?.fechaFin) params.append('fecha_fin', filtros.fechaFin)
      if (filtros?.granularidad) params.append('granularidad', filtros.granularidad)
      if (filtros?.categoria) params.append('categoria', filtros.categoria)

      const response = await api.get(`/api/reportes/graficos?${params.toString()}`)
      return response.data.map((punto: any) => this.normalizarDatosGrafico(punto))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener datos del gráfico')
    }
  },

  /**
   * Obtener reportes personalizados
   */
  async obtenerReportesPersonalizados(): Promise<ReportePersonalizado[]> {
    try {
      const response = await api.get('/api/reportes/personalizados')
      return response.data.map((reporte: any) => this.normalizarReporte(reporte))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener reportes personalizados')
    }
  },

  /**
   * Crear un reporte personalizado
   */
  async crearReporte(datos: Omit<ReportePersonalizado, 'id' | 'fechaCreacion' | 'ultimaEjecucion' | 'proximaEjecucion'>): Promise<ReportePersonalizado> {
    try {
      const response = await api.post('/api/reportes/personalizados', {
        nombre: datos.nombre,
        descripcion: datos.descripcion,
        tipo: datos.tipo,
        estado: datos.estado,
        frecuencia: datos.frecuencia,
        parametros: datos.parametros,
      })
      return this.normalizarReporte(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al crear reporte')
    }
  },

  /**
   * Actualizar un reporte personalizado
   */
  async actualizarReporte(id: string, datos: Partial<ReportePersonalizado>): Promise<ReportePersonalizado> {
    try {
      const response = await api.put(`/api/reportes/personalizados/${id}`, {
        nombre: datos.nombre,
        descripcion: datos.descripcion,
        tipo: datos.tipo,
        estado: datos.estado,
        frecuencia: datos.frecuencia,
        parametros: datos.parametros,
      })
      return this.normalizarReporte(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar reporte')
    }
  },

  /**
   * Eliminar un reporte personalizado
   */
  async eliminarReporte(id: string): Promise<void> {
    try {
      await api.delete(`/api/reportes/personalizados/${id}`)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al eliminar reporte')
    }
  },

  /**
   * Ejecutar un reporte personalizado
   */
  async ejecutarReporte(id: string): Promise<any> {
    try {
      const response = await api.post(`/api/reportes/personalizados/${id}/ejecutar`)
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al ejecutar reporte')
    }
  },

  /**
   * Exportar reporte
   */
  async exportarReporte(formato: 'pdf' | 'excel' | 'csv', filtros?: FiltrosReporte): Promise<Blob> {
    try {
      const params = new URLSearchParams()
      params.append('formato', formato)
      params.append('tipo', filtros?.tipo ?? filtros?.tipoReporte ?? 'general')
      if (filtros?.fechaInicio) params.append('fecha_inicio', filtros.fechaInicio)
      if (filtros?.fechaFin) params.append('fecha_fin', filtros.fechaFin)
      if (filtros?.categoria) params.append('categoria', filtros.categoria)

      const response = await api.get(`/api/reportes/exportar?${params.toString()}`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al exportar reporte')
    }
  },

  /**
   * Obtener actividad reciente
   */
  async obtenerActividadReciente(limite: number = 10): Promise<any[]> {
    try {
      const response = await api.get(`/api/reportes/actividad-reciente?limite=${limite}`)
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener actividad reciente')
    }
  },

  /**
   * Normalizar datos de métrica del backend al frontend
   */
  normalizarMetrica(metrica: any): MetricaReporte {
    return {
      id: String(metrica.id),
      titulo: metrica.titulo || metrica.title,
      nombre: metrica.nombre || metrica.titulo || metrica.name || metrica.title,
      valor: metrica.valor || metrica.value || 0,
      cambio: metrica.cambio || metrica.change || 0,
      tipo: metrica.tipo || metrica.type || 'actividad',
      icono: metrica.icono || metrica.icon || 'ChartBarIcon',
      color: metrica.color || '#3B82F6',
      unidad: String(metrica.unidad ?? metrica.unit ?? ''),
      descripcion: metrica.descripcion || metrica.description,
      valorAnterior: Number(metrica.valor_anterior ?? metrica.prev ?? metrica.previous ?? 0),
      tendencia: metrica.tendencia || metrica.trend || 'estable'
    }
  },

  /**
   * Normalizar datos de gráfico del backend al frontend
   */
  normalizarDatosGrafico(punto: any): DatosGrafico {
    return {
      fecha: punto.fecha || punto.date,
      valor: punto.valor || punto.value || 0,
      categoria: punto.categoria || punto.category,
      tipo: punto.tipo || punto.type,
      usuarios: Number(punto.usuarios ?? 0),
      descargas: Number(punto.descargas ?? 0),
      uploads: Number(punto.uploads ?? 0),
      ingresos: Number(punto.ingresos ?? 0),
      sesiones: Number(punto.sesiones ?? 0),
    }
  },

  /**
   * Normalizar datos de reporte del backend al frontend
   */
  normalizarReporte(reporte: any): ReportePersonalizado {
    return {
      id: String(reporte.id),
      nombre: reporte.nombre || reporte.name,
      descripcion: reporte.descripcion || reporte.description,
      tipo: reporte.tipo || reporte.type || 'actividad',
      estado: reporte.estado || reporte.status || 'activo',
      fechaCreacion: reporte.fecha_creacion || reporte.created_at || new Date().toISOString(),
      ultimaEjecucion: reporte.ultima_ejecucion || reporte.last_execution,
      proximaEjecucion: reporte.proxima_ejecucion || reporte.next_execution,
      frecuencia: reporte.frecuencia || reporte.frequency || 'manual',
      parametros: reporte.parametros || reporte.parameters || {},
      resultados: reporte.resultados || reporte.results,
    }
  },
}
